import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import { IDefaultResponse } from "../../models/DefaultResponse";
import {
  IPinnacleAffectedParts,
  IPinnacleCustomerUsage,
  IPinnacleItemType,
  IPinnacleSuggestion,
} from "../../models/PinnacleMapping/PinnacleMapping";
import { PinnacleMappingQueryKeys } from "./keys";

/**
 * Barney answers the customer routes with 200 and success false when the customer's Core has
 * nothing to give (not set up, unreachable, not deployed yet). That is a state to show, so it is
 * thrown with the reason, as on the sync tracker.
 */
function unwrap<T>(res: IDefaultResponse<T>): T {
  if (!res.success) throw new Error(res.message || "Nothing came back from this customer's Core.");
  return res.data;
}

export function usePinnacleItemTypes() {
  const { get } = useApiHelper();

  return useQuery({
    queryKey: PinnacleMappingQueryKeys.itemTypes(),
    queryFn: () => get<IDefaultResponse<IPinnacleItemType[]>>("/pinnacle-mapping/item-types").then(unwrap),
  });
}

export function usePinnacleSuggestions(itemTypeId?: string) {
  const { get } = useApiHelper();

  return useQuery({
    queryKey: PinnacleMappingQueryKeys.suggestions(itemTypeId ?? ""),
    queryFn: () =>
      get<IDefaultResponse<IPinnacleSuggestion[]>>(`/pinnacle-mapping/item-types/${itemTypeId}/suggestions`).then(unwrap),
    enabled: !!itemTypeId,
  });
}

/**
 * Item types in the customer's imported parts, with counts. Barney adds any it has not seen before
 * to the list, so the list is refreshed when it reports new ones.
 */
export function usePinnacleUsage(customerId?: string) {
  const { get } = useApiHelper();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: PinnacleMappingQueryKeys.usage(customerId ?? ""),
    queryFn: () =>
      get<IDefaultResponse<IPinnacleCustomerUsage>>(`/pinnacle-mapping/customer/${customerId}/usage`).then(unwrap),
    enabled: !!customerId,
    staleTime: 60_000,
  });

  const added = query.data?.itemTypesAdded ?? 0;

  useEffect(() => {
    if (added > 0) queryClient.invalidateQueries({ queryKey: PinnacleMappingQueryKeys.itemTypes() });
  }, [added, query.dataUpdatedAt]);

  return query;
}

/** Where each part of the item type would go under the mappings as they stand. Changes nothing. */
export function usePinnacleAffectedParts(customerId?: string, itemTypeId?: string) {
  const { post } = useApiHelper();

  return useQuery({
    queryKey: PinnacleMappingQueryKeys.affectedParts(customerId ?? "", itemTypeId ?? ""),
    queryFn: () =>
      post<IDefaultResponse<IPinnacleAffectedParts>>(`/pinnacle-mapping/customer/${customerId}/affected-parts`, {
        itemTypeId,
        limit: 1000,
      }).then(unwrap),
    enabled: !!customerId && !!itemTypeId,
  });
}
